export interface RestrictionOrificeCommonInputs {
  density: number;
  orificeCoefficient: number;
}

export interface RestrictionOrificeFlowInputs
  extends RestrictionOrificeCommonInputs {
  orificeDiameter: number;
  upstreamPressure: number;
  downstreamPressure: number;
}

export interface RestrictionOrificeDiameterInputs
  extends RestrictionOrificeCommonInputs {
  flow: number;
  upstreamPressure: number;
  downstreamPressure: number;
}

export interface RestrictionOrificeUpstreamPressureInputs
  extends RestrictionOrificeCommonInputs {
  orificeDiameter: number;
  flow: number;
  downstreamPressure: number;
}

export interface RestrictionOrificeDownstreamPressureInputs
  extends RestrictionOrificeCommonInputs {
  orificeDiameter: number;
  flow: number;
  upstreamPressure: number;
}

const hasValidCommonInputs = (
  input: RestrictionOrificeCommonInputs
) =>
  Number.isFinite(input.density) &&
  input.density > 0 &&
  Number.isFinite(input.orificeCoefficient) &&
  input.orificeCoefficient > 0;

const orificeArea = (orificeDiameter: number) =>
  (Math.PI * orificeDiameter * orificeDiameter) / 4;

export const calculateRestrictionOrificeFlow = (
  input: RestrictionOrificeFlowInputs
) => {
  if (
    !hasValidCommonInputs(input) ||
    !Number.isFinite(input.orificeDiameter) ||
    input.orificeDiameter <= 0 ||
    !Number.isFinite(input.upstreamPressure) ||
    !Number.isFinite(input.downstreamPressure) ||
    input.upstreamPressure <= input.downstreamPressure
  ) {
    return null;
  }

  return (
    input.orificeCoefficient *
    orificeArea(input.orificeDiameter) *
    Math.sqrt(
      (2 * (input.upstreamPressure - input.downstreamPressure)) /
        input.density
    )
  );
};

export const calculateRestrictionOrificeDiameter = (
  input: RestrictionOrificeDiameterInputs
) => {
  if (
    !hasValidCommonInputs(input) ||
    !Number.isFinite(input.flow) ||
    input.flow < 0 ||
    !Number.isFinite(input.upstreamPressure) ||
    !Number.isFinite(input.downstreamPressure) ||
    input.upstreamPressure <= input.downstreamPressure
  ) {
    return null;
  }

  return Math.sqrt(
    ((4 / Math.PI) * input.flow) /
      (input.orificeCoefficient *
        Math.sqrt(
          (2 *
            (input.upstreamPressure - input.downstreamPressure)) /
            input.density
        ))
  );
};

export const calculateRestrictionOrificeUpstreamPressure = (
  input: RestrictionOrificeUpstreamPressureInputs
) => {
  if (
    !hasValidCommonInputs(input) ||
    !Number.isFinite(input.orificeDiameter) ||
    input.orificeDiameter <= 0 ||
    !Number.isFinite(input.flow) ||
    input.flow < 0 ||
    !Number.isFinite(input.downstreamPressure)
  ) {
    return null;
  }

  return (
    Math.pow(
      input.flow /
        (input.orificeCoefficient *
          orificeArea(input.orificeDiameter)),
      2
    ) *
      (input.density / 2) +
    input.downstreamPressure
  );
};

export const calculateRestrictionOrificeDownstreamPressure = (
  input: RestrictionOrificeDownstreamPressureInputs
) => {
  if (
    !hasValidCommonInputs(input) ||
    !Number.isFinite(input.orificeDiameter) ||
    input.orificeDiameter <= 0 ||
    !Number.isFinite(input.flow) ||
    input.flow < 0 ||
    !Number.isFinite(input.upstreamPressure)
  ) {
    return null;
  }

  return (
    input.upstreamPressure -
    Math.pow(
      input.flow /
        (input.orificeCoefficient *
          orificeArea(input.orificeDiameter)),
      2
    ) *
      (input.density / 2)
  );
};
